// theme-shadcn.js - Shadcn/Tailwind based theme system
//
// This file provides:
// 1. CustomThemeProvider - Root provider that sets CSS variables
// 2. useCustomTheme() - Hook for theme mode management (mode, actualMode, setMode)
//
// Note: Colors, typography, and z-index are now provided via CSS variables.
// Use CSS variables (e.g., hsl(var(--primary))) instead of palette object.
// The palette, typography, and zIndex objects are kept for backward compatibility only.
import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useLayoutEffect,
} from "react";
import { useRecoilValue } from "recoil";
import { primaryColorState } from "./global-state";
import { hexToHsl, adjustLightness } from "./lib/color-utils";

export const ThemeContext = createContext(null);

// Primary color options
export const primaryColors = {
  default: { light: "#475B63", dark: "#8fbdcf" },
  blue: { light: "#1976D2", dark: "#64B5F6" },
  purple: { light: "#7B1FA2", dark: "#BA68C8" },
  green: { light: "#388E3C", dark: "#81C784" },
  orange: { light: "#F57C00", dark: "#FFB74D" },
  teal: { light: "#00796B", dark: "#4DB6AC" },
  pink: { light: "#C2185B", dark: "#F06292" },
};

// Background colors that complement each primary color
export const backgroundColors = {
  default: { light: "#EAEEF0", dark: "#1a1a1a" },
  blue: { light: "#E3F2FD", dark: "#0D1B2A" },
  purple: { light: "#F3E5F5", dark: "#1A0D1F" },
  green: { light: "#E8F5E9", dark: "#0D1F0E" },
  orange: { light: "#FFF3E0", dark: "#1F1508" },
  teal: { light: "#E0F2F1", dark: "#0D1F1C" },
  pink: { light: "#FCE4EC", dark: "#1F0D15" },
};

// Light theme color values
const lightThemeColors = {
  primary: { main: "#475B63" },
  secondary: { main: "#bee7f7" },
  error: { main: "#ed7e7e" },
  warning: {
    main: "#FFD700",
    dark: "#f57c00",
    light: "#ffb74d",
    contrastText: "rgba(0, 0, 0, 0.87)",
  },
  success: {
    main: "#4caf50",
    dark: "#388e3c",
    light: "#81c784",
    contrastText: "#fff",
  },
  info: {
    main: "#2196f3",
    dark: "#1976d2",
    light: "#64b5f6",
    contrastText: "#fff",
  },
  background: {
    default: "#EAEEF0",
    white: "#FFFFFF",
    disabled: "#BDBDBD",
    button: "#475B63",
    pinned: "#f5e6b0",
    nav: "#D3E1E8",
    card: "#FFFFFF",
    paper: "#FFFFFF",
    panel: "#f2fbff",
    dialog: "#d19191",
    detail: "#F5F5F5",
    notification: "#EAEEF0",
    automationChip: "rgba(71, 91, 99, 0.2)",
    input: "#FFFFFF",
    activeInput: "#edf3f5",
    dataGrid: "#FFFFFF",
    chatCanvas: "#F8FAFC",
    instructionInput: "#FFFFFF",
    assistantUserMessage: "#f2fbff",
    assistantServiceMessage: "#f7db94",
    assistantNotificationMessage: "#9fe3a8",
    assistantDialogMessage: "#f7db94",
    log2: "linear-gradient(135deg, rgba(30, 90, 180, 0.6) 0%, rgba(0, 150, 200, 0.6) 100%)",
    logo1: "#475B63",
    logo2: "#8fbdcf",
    l1: "#EAEEF0",
    l2: "#9fe3a8",
    l3: "#e1c9a8",
    l4: "#d19191",
    l5: "#475B63",
    l6: "#8fbdcf",
    l0: "#FFFFFF",
    c1: "#9fe3a8",
    c2: "#e1c9a8",
    c3: "#d19191",
    c0: "#FFFFFF",
    colorfulGradientSky:
      "linear-gradient(135deg, rgb(195, 215, 235) 0%, rgb(205, 225, 240) 25%, rgb(185, 210, 245) 50%, rgb(200, 220, 245) 75%, rgb(190, 215, 240) 100%)",
    colorfulGradientAurora:
      "linear-gradient(135deg, rgb(170, 221, 216) 0%, rgb(191, 228, 191) 25%, rgb(214, 222, 223) 50%, rgb(211, 156, 206) 75%, rgb(166, 211, 203) 100%)",
    colorfulGradientCoral:
      "linear-gradient(135deg, rgb(240, 176, 150) 0%, rgb(240, 196, 204) 25%, rgb(240, 195, 170) 50%, rgb(240, 169, 203) 75%, rgb(240, 224, 209) 100%)",
    colorfulGradientLavender:
      "linear-gradient(135deg, rgb(232, 216, 232) 0%, rgb(237, 237, 240) 25%, rgb(235, 230, 235) 50%, rgb(227, 211, 230) 75%, rgb(230, 232, 237) 100%)",
    colorfulGradientMint:
      "linear-gradient(135deg, rgb(223, 238, 223) 0%, rgb(227, 236, 236) 25%, rgb(222, 236, 222) 50%, rgb(215, 228, 225) 75%, rgb(209, 229, 227) 100%)",
    colorfulGradientRose:
      "linear-gradient(135deg, rgb(240, 212, 218) 0%, rgb(240, 215, 221) 25%, rgb(240, 199, 171) 50%, rgb(240, 188, 163) 75%, rgb(240, 231, 230) 100%)",
    colorfulGradientGalaxy:
      "linear-gradient(135deg, rgb(157, 157, 174) 0%, rgb(185, 153, 216) 20%, rgb(240, 132, 169) 40%, rgb(163, 214, 240) 60%, rgb(215, 157, 206) 80%, rgb(159, 121, 160) 100%)",
    colorfulGradientGolden:
      "linear-gradient(135deg, rgb(240, 221, 164) 0%, rgb(240, 223, 209) 25%, rgb(240, 226, 201) 50%, rgb(240, 199, 164) 75%, rgb(240, 215, 167) 100%)",
  },
  icon: {
    y1: "#FFD700",
    y1_1: "#B0B0B0",
  },
  text: {
    primary: "#333333",
    secondary: "#757575",
    tertiary: "#FFFFFF",
    white: "#FFFFFF",
    disabled: "#9E9E9E",
    warning: "#FFD700",
    error: "#ed7e7e",
    l1: "#475B63",
    l2: "#f7b457",
    l4: "#e05e5e",
  },
  mentionChip: {
    background: "#E3F2FD",
    text: "#1976D2",
    x: "#757575",
  },
  border: {
    input: "rgba(71, 91, 99, 0.5)",
    inputFocus: "rgba(71, 91, 99, 1)",
  },
  divider: "rgba(0, 0, 0, 0.12)",
  action: {
    hover: "rgba(0, 0, 0, 0.04)",
  },
};

// Dark theme color values
const darkThemeColors = {
  primary: {
    main: "#8fbdcf",
    dark: "#475B63",
    light: "#bee7f7",
    contrastText: "#333333",
  },
  secondary: {
    main: "#30434a",
    dark: "#1a1a1a",
    light: "#475B63",
    contrastText: "#FFFFFF",
  },
  error: {
    main: "#ed7e7e",
    dark: "#c62828",
    light: "#ef5350",
    contrastText: "#FFFFFF",
  },
  warning: {
    main: "#FFD700",
    dark: "#f57c00",
    light: "#ffb74d",
    contrastText: "#333333",
  },
  success: {
    main: "#66bb6a",
    dark: "#388e3c",
    light: "#81c784",
    contrastText: "#fff",
  },
  background: {
    default: "#1a1a1a",
    white: "#2d2d2d",
    disabled: "#404040",
    pinned: "#b09228",
    button: "#8fbdcf",
    nav: "#2d3943",
    card: "#2d2d2d",
    paper: "#101010",
    dialog: "#3d2828",
    notification: "#2d2d2d",
    automationChip: "rgba(255, 255, 255, 0.8)",
    input: "#2d2d2d",
    activeInput: "#323e42",
    dataGrid: "#2d2d2d",
    instructionInput: "#2d2d2d",
    assistantUserMessage: "#36444a",
    assistantServiceMessage: "#7d6b3c",
    assistantNotificationMessage: "#47804f",
    assistantDialogMessage: "#9e7e39",
    log2: "linear-gradient(135deg, rgba(30, 90, 180, 0.8) 0%, rgba(0, 150, 200, 0.8) 100%)",
    logo1: "#8fbdcf",
    logo2: "#475B63",
    l1: "#1a1a1a",
    l2: "#47804f",
    l3: "#c7a87d",
    l4: "#d19191",
    l5: "#475B63",
    l6: "#8fbdcf",
    l0: "#FFFFFF",
    c1: "#47804f",
    c2: "#c7a87d",
    c3: "#d19191",
    c0: "#FFFFFF",
    colorfulGradientSky:
      "linear-gradient(135deg, rgb(35, 55, 75) 0%, rgb(40, 60, 80) 25%, rgb(30, 50, 85) 50%, rgb(45, 65, 85) 75%, rgb(35, 60, 80) 100%)",
    colorfulGradientAurora:
      "linear-gradient(135deg, rgb(42, 98, 93) 0%, rgb(70, 103, 70) 25%, rgb(82, 89, 98) 50%, rgb(85, 50, 94) 75%, rgb(45, 93, 91) 100%)",
    colorfulGradientCoral:
      "linear-gradient(135deg, rgb(84, 52, 40) 0%, rgb(84, 66, 68) 25%, rgb(84, 60, 51) 50%, rgb(84, 46, 65) 75%, rgb(84, 75, 66) 100%)",
    colorfulGradientLavender:
      "linear-gradient(135deg, rgb(42, 36, 42) 0%, rgb(43, 43, 45) 25%, rgb(42, 39, 42) 50%, rgb(35, 31, 42) 75%, rgb(37, 42, 43) 100%)",
    colorfulGradientMint:
      "linear-gradient(135deg, rgb(43, 58, 43) 0%, rgb(46, 56, 56) 25%, rgb(42, 56, 42) 50%, rgb(35, 51, 46) 75%, rgb(30, 54, 51) 100%)",
    colorfulGradientRose:
      "linear-gradient(135deg, rgb(71, 56, 59) 0%, rgb(71, 58, 61) 25%, rgb(71, 52, 44) 50%, rgb(70, 46, 43) 75%, rgb(71, 66, 65) 100%)",
    colorfulGradientGalaxy:
      "linear-gradient(135deg, rgb(29, 29, 68) 0%, rgb(89, 42, 133) 15%, rgb(122, 28, 79) 35%, rgb(20, 116, 148) 55%, rgb(79, 65, 108) 75%, rgb(73, 20, 111) 100%)",
    colorfulGradientGolden:
      "linear-gradient(135deg, rgb(97, 85, 20) 0%, rgb(97, 86, 76) 25%, rgb(97, 88, 74) 50%, rgb(97, 70, 20) 75%, rgb(97, 78, 22) 100%)",
  },
  icon: {
    y1: "#FFD700",
    y1_1: "#B0B0B0",
  },
  text: {
    primary: "#ffffff",
    secondary: "#bdbdbd",
    tertiary: "#333333",
    white: "#FFFFFF",
    disabled: "#707070",
    warning: "#FFD700",
    error: "#ed7e7e",
    l1: "#475B63",
    l2: "#c28d44",
    l4: "#d19191",
  },
  mentionChip: {
    background: "#4F6B80",
    text: "#BFDBFE",
    x: "#94A3B8",
  },
  border: {
    input: "rgba(143, 189, 207, 0.5)",
    inputFocus: "rgba(143, 189, 207, 1)",
  },
  divider: "rgba(255, 255, 255, 0.12)",
  action: {
    hover: "rgba(255, 255, 255, 0.08)",
  },
};

// Typography values
const typography = {
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
  h1: {
    fontSize: "2.5rem",
    fontWeight: 300,
    lineHeight: 1.2,
    letterSpacing: "-0.01562em",
  },
  h2: {
    fontSize: "2rem",
    fontWeight: 300,
    lineHeight: 1.2,
    letterSpacing: "-0.00833em",
  },
  h3: {
    fontSize: "1.75rem",
    fontWeight: 400,
    lineHeight: 1.2,
    letterSpacing: "0em",
  },
  h4: {
    fontSize: "1.5rem",
    fontWeight: 400,
    lineHeight: 1.235,
    letterSpacing: "0.00735em",
  },
  h5: {
    fontSize: "1.25rem",
    fontWeight: 400,
    lineHeight: 1.334,
    letterSpacing: "0em",
  },
  h6: {
    fontSize: "1.25rem",
    fontWeight: 500,
    lineHeight: 1.6,
    letterSpacing: "0.0075em",
  },
  subtitle1: {
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: 1.75,
    letterSpacing: "0.00938em",
  },
  subtitle2: {
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: 1.57,
    letterSpacing: "0.00714em",
  },
  body1: {
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: "0.00938em",
  },
  body2: {
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.43,
    letterSpacing: "0.01071em",
  },
  button: {
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: 1.75,
    letterSpacing: "0.02857em",
    textTransform: "uppercase",
  },
  caption: {
    fontSize: "0.75rem",
    fontWeight: 400,
    lineHeight: 1.66,
    letterSpacing: "0.03333em",
  },
  overline: {
    fontSize: "0.75rem",
    fontWeight: 400,
    lineHeight: 2.66,
    letterSpacing: "0.08333em",
    textTransform: "uppercase",
  },
};

// Z-index values
const zIndex = {
  mobileStepper: 1000,
  fab: 1050,
  speedDial: 1050,
  appBar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 4400,
  tooltip: 1500,
  automationDrawer: 1400,
  automationInput: 1450,
  importantToast: 4400,
  dashboardInput: 600,
  dashboardDrag: 650,
  snackbarInput: 4500,
};

// Helper function to convert camelCase to kebab-case
const toKebabCase = (str) => {
  return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, "$1-$2").toLowerCase();
};

// Set CSS variables on root element
// Now sets both shadcn/ui standard variables (HSL format) and legacy variables (for backward compatibility)
const setCSSVariables = (colors, isDark) => {
  const root = document.documentElement;

  // Set dark mode class
  if (isDark) {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }

  // Convert colors to HSL for shadcn/ui standard variables
  const primaryHsl = hexToHsl(colors.primary.main);
  const secondaryHsl = hexToHsl(colors.secondary.main);
  const backgroundHsl = hexToHsl(colors.background.default);
  const foregroundHsl = hexToHsl(colors.text.primary);
  const cardHsl = hexToHsl(
    colors.background.card || colors.background.paper || "#FFFFFF"
  );
  const mutedHsl = hexToHsl(colors.text.secondary);
  // Calculate border HSL - handle rgba format
  let borderHsl = "240 5.9% 90%";
  if (colors.border?.input) {
    if (colors.border.input.includes("rgba")) {
      const rgbaMatch = colors.border.input.match(
        /rgba?\((\d+),\s*(\d+),\s*(\d+)/
      );
      if (rgbaMatch) {
        borderHsl = hexToHsl(
          "#" +
            parseInt(rgbaMatch[1]).toString(16).padStart(2, "0") +
            parseInt(rgbaMatch[2]).toString(16).padStart(2, "0") +
            parseInt(rgbaMatch[3]).toString(16).padStart(2, "0")
        );
      }
    } else {
      borderHsl = hexToHsl(colors.border.input);
    }
  }
  const destructiveHsl = colors.error?.main
    ? hexToHsl(colors.error.main)
    : "0 84.2% 60.2%";

  // Set shadcn/ui standard variables (HSL format) - professional standard only
  root.style.setProperty("--background", backgroundHsl);
  root.style.setProperty("--foreground", foregroundHsl);
  root.style.setProperty("--card", cardHsl);
  root.style.setProperty("--card-foreground", foregroundHsl);
  root.style.setProperty("--popover", cardHsl);
  root.style.setProperty("--popover-foreground", foregroundHsl);
  root.style.setProperty("--primary", primaryHsl);
  root.style.setProperty(
    "--primary-foreground",
    isDark ? "0 0% 98%" : "0 0% 98%"
  );
  root.style.setProperty("--secondary", secondaryHsl);
  root.style.setProperty("--secondary-foreground", foregroundHsl);
  root.style.setProperty(
    "--muted",
    adjustLightness(backgroundHsl, isDark ? -5 : 5)
  );
  root.style.setProperty("--muted-foreground", mutedHsl);
  root.style.setProperty(
    "--accent",
    adjustLightness(backgroundHsl, isDark ? -3 : 3)
  );
  root.style.setProperty("--accent-foreground", foregroundHsl);
  root.style.setProperty("--destructive", destructiveHsl);
  root.style.setProperty("--destructive-foreground", "0 0% 98%");
  root.style.setProperty("--border", borderHsl);
  root.style.setProperty("--input", borderHsl);
  root.style.setProperty("--ring", primaryHsl);
  root.style.setProperty("--radius", "0.5rem");

  // Set z-index values (project-specific, but needed)
  Object.keys(zIndex).forEach((key) => {
    const value = zIndex[key];
    const cssKey = toKebabCase(key);
    root.style.setProperty(`--z-index-${cssKey}`, String(value));
  });
};

export const CustomThemeProvider = ({ children }) => {
  const primaryColorKey = useRecoilValue(primaryColorState);
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem("themeMode") || "auto";
    } catch {
      return "auto";
    }
  });
  const [prefersDarkMode, setPrefersDarkMode] = useState(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }
    return false;
  });

  useEffect(() => {
    try {
      localStorage.setItem("themeMode", mode);
    } catch (error) {
      console.warn("Could not save theme mode to localStorage:", error);
    }
  }, [mode]);

  // Listen to system theme changes only when mode is "auto"
  useEffect(() => {
    if (mode !== "auto") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => setPrefersDarkMode(e.matches);

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [mode]);

  const actualMode = useMemo(() => {
    return mode === "auto" ? (prefersDarkMode ? "dark" : "light") : mode;
  }, [mode, prefersDarkMode]);

  // Get theme colors based on mode and primary color
  const themeColors = useMemo(() => {
    const colorScheme = primaryColors[primaryColorKey] || primaryColors.default;
    const bgColorScheme =
      backgroundColors[primaryColorKey] || backgroundColors.default;

    const baseColors =
      actualMode === "dark" ? darkThemeColors : lightThemeColors;
    const colors = JSON.parse(JSON.stringify(baseColors)); // Deep clone

    // Override primary color
    colors.primary.main =
      actualMode === "dark" ? colorScheme.dark : colorScheme.light;

    // Override background default
    colors.background.default =
      actualMode === "dark" ? bgColorScheme.dark : bgColorScheme.light;

    // Override logo colors
    if (actualMode === "dark") {
      colors.background.logo1 = colorScheme.dark;
      colors.background.logo2 = colorScheme.light;
    } else {
      colors.background.logo1 = colorScheme.light;
      colors.background.logo2 = colorScheme.dark;
    }

    return colors;
  }, [actualMode, primaryColorKey]);

  // Set CSS variables immediately before render
  useLayoutEffect(() => {
    // Ensure themeColors is valid before setting CSS variables
    if (themeColors && themeColors.background) {
      setCSSVariables(themeColors, actualMode === "dark");

      // Dispatch a custom event to notify all components about theme change
      // This ensures components that directly check document.documentElement.classList
      // can also respond to theme changes
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("themechange", {
            detail: { mode: actualMode, isDark: actualMode === "dark" },
          })
        );
      }
    } else {
      console.error("Theme colors not initialized properly", themeColors);
    }
  }, [themeColors, actualMode]);

  // Create a theme-like object for backward compatibility
  const theme = useMemo(
    () => ({
      palette: themeColors,
      typography,
      zIndex,
      mode: actualMode,
    }),
    [themeColors, actualMode]
  );

  const contextValue = useMemo(
    () => ({
      mode, // user selected mode (can be "auto", "light", "dark")
      actualMode, // resolved mode (only "light" or "dark")
      setMode,
      // Deprecated: Use CSS variables instead (e.g., hsl(var(--primary)), hsl(var(--background)))
      // Kept for backward compatibility with dynamic components that may need minimal theme object
      palette: themeColors,
      typography,
      zIndex,
      theme, // For backward compatibility
    }),
    [mode, actualMode, themeColors, setMode]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to access theme context
 *
 * @returns {Object} Theme context with:
 *   - mode: User selected mode ("auto", "light", "dark")
 *   - actualMode: Resolved mode ("light" or "dark")
 *   - setMode: Function to change theme mode
 *   - palette: (Deprecated) Use CSS variables instead (e.g., hsl(var(--primary)))
 *   - typography: (Deprecated) Use Tailwind CSS classes instead
 *   - zIndex: (Deprecated) Use CSS variables instead (e.g., --z-index-modal)
 *   - theme: (Deprecated) For backward compatibility only
 *
 * @example
 * // Recommended: Use CSS variables
 * const color = "hsl(var(--primary))";
 *
 * // For theme mode switching only
 * const { mode, actualMode, setMode } = useCustomTheme();
 */
export const useCustomTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    console.error("useCustomTheme must be used within a CustomThemeProvider");
    // Return a default context to prevent crashes
    return {
      mode: "light",
      actualMode: "light",
      palette: {},
      typography: {},
      zIndex: {},
      setMode: () => {},
    };
  }
  return context;
};

/**
 * Hook to get CSS variable values and theme info
 *
 * @deprecated This hook is deprecated. Use CSS variables directly or useCustomTheme() for mode switching.
 *
 * @returns {Object} Theme info with helper to get CSS variables
 *
 * @example
 * // Recommended: Use CSS variables directly
 * const color = "hsl(var(--primary))";
 *
 * // Or use getComputedStyle directly if needed
 * const cssVar = getComputedStyle(document.documentElement)
 *   .getPropertyValue("--primary")
 *   .trim();
 */
export const useTheme = () => {
  const { palette, typography, zIndex, actualMode } = useCustomTheme();

  return {
    palette,
    typography,
    zIndex,
    mode: actualMode,
    // Helper to get CSS variable value
    getCSSVar: (varName) => {
      return getComputedStyle(document.documentElement)
        .getPropertyValue(varName)
        .trim();
    },
  };
};
